import ScrollReveal from './ScrollReveal';
import type { HeroProduct } from './heroProducts';

interface ProductGridProps {
    products: HeroProduct[];
}

/**
 * Static product grid. Rendered instead of the 3D orbit when WebGL is unavailable, when the visitor
 * prefers reduced motion, and during the build-time prerender snapshot.
 */
export default function ProductGrid({ products }: ProductGridProps) {
    return (
        <div className="grid grid-cols-3 gap-3 w-full sm:grid-cols-3 lg:grid-cols-2 lg:gap-4 max-w-md xl:max-w-lg mx-auto lg:mx-0">
            {products.map((product, i) => (
                <ScrollReveal key={product.name} variant="fade-up" delay={300 + i * 100} distance={24}>
                    <a
                        href={product.comingSoon ? undefined : product.href}
                        target={product.comingSoon ? undefined : '_blank'}
                        rel={product.comingSoon ? undefined : 'noopener noreferrer'}
                        className={`group relative bg-white/60 rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 xl:p-8 flex flex-col items-center justify-center gap-1.5 sm:gap-2 lg:gap-3 aspect-4/3 transition-all duration-300 hover:bg-white hover:shadow-lg ${product.comingSoon ? 'cursor-default' : ''}`}
                    >
                        {product.comingSoon && (
                            <span className="absolute top-1.5 right-1.5 lg:top-3 lg:right-3 bg-primary/10 text-primary text-[8px] lg:text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                Coming Soon
                            </span>
                        )}
                        <img
                            src={product.logo}
                            alt={`${product.prefix} ${product.name}`}
                            className="h-8 w-8 sm:h-10 sm:w-10 lg:h-14 lg:w-14 xl:h-16 xl:w-16 object-contain grayscale opacity-60 transition-all duration-300 group-hover:grayscale-0 group-hover:opacity-100"
                        />
                        <p className="w-full truncate text-center text-[11px] sm:text-xs lg:text-sm xl:text-base font-semibold tracking-tight">
                            <span className="text-secondary">{product.prefix} </span>
                            <span className="text-secondary font-extrabold">{product.name}</span>
                        </p>
                    </a>
                </ScrollReveal>
            ))}
        </div>
    );
}

/**
 * Keyboard- and crawler-reachable product links that shadow the 3D canvas. The canvas itself paints
 * to a single `aria-hidden` element, so this list is what assistive tech and search engines consume.
 */
export function ProductLinksScreenReader({ products }: ProductGridProps) {
    return (
        <ul className="sr-only">
            {products.map((product) => (
                <li key={product.name}>
                    {product.comingSoon ? (
                        <span>{`${product.prefix} ${product.name} — coming soon`}</span>
                    ) : (
                        <a href={product.href} target="_blank" rel="noopener noreferrer">
                            {`${product.prefix} ${product.name}`}
                        </a>
                    )}
                </li>
            ))}
        </ul>
    );
}
